import { SYNC_STALE_AFTER_HOURS, formatSyncAge, lastSyncAgeHours } from './mailboxStatusDisplay';

export type TodaySyncKind = 'attention' | 'unknown' | 'checking' | 'connect' | 'syncing' | 'stale' | 'never' | 'synced';
export type TodaySyncTone = 'warning' | 'neutral' | 'connect' | 'success';

export interface TodaySyncButtonInput {
    /** A mailbox sync failed or its grant needs reconnecting (`anyFailed || anyGrantAttention`). */
    needsAttention: boolean;
    /** At least one GET /mailboxes succeeded (`pollSucceeded`). */
    rowsKnown: boolean;
    /** Mailbox rows when known, else `/user/me`'s `google_connected`. */
    connected: boolean;
    /** A sync job is running (`anyActive`). */
    syncing: boolean;
    /** `last_successful_sync` of each CONNECTED row (`connectedSyncTimes`). */
    connectedSyncTimes: Array<string | null | undefined>;
    now?: Date;
}

export interface TodaySyncButtonState {
    kind: TodaySyncKind;
    tone: TodaySyncTone;
    label: string;
    ariaLabel: string;
}

const OPEN = 'Open mail settings';

function state(kind: TodaySyncKind, tone: TodaySyncTone, label: string, detail?: string): TodaySyncButtonState {
    const ariaLabel = detail ? `${label}. ${detail}. ${OPEN}` : `${label}. ${OPEN}`;
    return { kind, tone, label, ariaLabel };
}

/**
 * The Today header's mail button. First match wins: a needed fix beats everything (a
 * provider-revoked sole mailbox has no connected row but still needs Fix sync); without a
 * mailbox snapshot the page only knows "connected", so it never claims Synced, and a
 * not-connected `/user/me` is not trusted either (a grant that needs reconnecting reads as
 * not connected there), so Connect waits for the mailbox rows; the oldest connected mailbox
 * decides Stale by the app's 24 h rule (`SYNC_STALE_AFTER_HOURS`).
 */
export function getTodaySyncButtonState(input: TodaySyncButtonInput): TodaySyncButtonState {
    const { needsAttention, rowsKnown, connected, syncing, connectedSyncTimes, now = new Date() } = input;

    if (needsAttention) return state('attention', 'warning', 'Fix sync', 'Mail sync needs attention');
    if (!rowsKnown && connected) return state('unknown', 'neutral', 'Mail connected');
    if (!rowsKnown) return state('checking', 'neutral', 'Checking mail');
    if (!connected) return { kind: 'connect', tone: 'connect', label: 'Connect', ariaLabel: 'Connect mail' };
    if (syncing) return state('syncing', 'success', 'Syncing', 'Mail sync is running');

    const ages = connectedSyncTimes
        .map((t) => lastSyncAgeHours(t, now))
        .filter((h): h is number => h !== null);
    if (ages.length === 0) return state('never', 'neutral', 'Not synced yet', 'Mail has not finished a sync');

    const oldest = Math.max(...ages);
    if (oldest >= SYNC_STALE_AFTER_HOURS) {
        const age = formatSyncAge(oldest);
        return state('stale', 'warning', `Stale (${age})`, `Last mail sync was ${age}`);
    }
    return state('synced', 'success', 'Synced', 'Mail is up to date');
}
